import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { EALogo } from "@/components/brand/EALogo";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user.role === "admin") navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/admin");
    else setError(res.error || "Login failed");
  };

  return (
    <div className="min-h-screen bg-ink-950 grid-bg text-white flex flex-col" data-testid="admin-login-page">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="aurora-blob animate-aurora" style={{ background: "#00F0FF", width: 500, height: 500, top: "-10%", left: "10%" }} />
        <div className="aurora-blob animate-aurora-slow" style={{ background: "#8A2BE2", width: 500, height: 500, bottom: "-10%", right: "10%" }} />
      </div>

      <header className="relative z-10 px-6 md:px-10 py-6 flex items-center justify-between border-b border-white/5">
        <Link to="/" className="flex items-center gap-3" data-testid="admin-login-home">
          <span className="text-spectrum-green"><EALogo size={28} strokeWidth={7} /></span>
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/60">Svenska Invest / EA · Admin</span>
        </Link>
        <Link to="/" className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/60 hover:text-white">← back</Link>
      </header>

      <main className="relative z-10 flex-1 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md border border-white/10 bg-ink-900 relative overflow-hidden"
        >
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-spectrum-green/20 blur-3xl pointer-events-none" />
          <div className="relative p-8 md:p-10">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-spectrum-green mb-3">◢ Restricted</div>
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-8">Admin login</h1>
            <form onSubmit={submit} className="space-y-5" data-testid="admin-login-form">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Email</div>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="erik@svenskainvest.com"
                  autoComplete="username"
                  className="terminal-input bg-ink-950 border border-white/15 rounded-none font-mono text-sm text-white h-11"
                  data-testid="admin-login-email"
                />
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Password</div>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  className="terminal-input bg-ink-950 border border-white/15 rounded-none font-mono text-sm text-white h-11"
                  data-testid="admin-login-password"
                />
              </div>
              {error && (
                <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-red-400" data-testid="admin-login-error">
                  ◈ {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-none bg-spectrum-green text-ink-950 hover:bg-white font-mono text-[11px] uppercase tracking-[0.3em] disabled:opacity-60"
                data-testid="admin-login-submit"
              >
                {loading ? "Authenticating..." : "Enter →"}
              </Button>
            </form>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
